import {
  ArrowUpTrayIcon,
  CheckCircleIcon,
  DocumentIcon,
  DocumentPlusIcon,
  MicrophoneIcon,
} from "@heroicons/react/24/outline";
import { useRef } from "react";
import cn from "classnames";
import Button from "./Button";
import Tooltip from "./Tooltip";

interface NewCaseDropdownProps {
  onRecordAudio: () => void;
  onUploadAudio: () => void;
  onBlankReport: () => void;
  showChecklist?: boolean;
  onChecklist?: () => void;
  buttonText?: string;
  buttonVariant?: "primary" | "secondary" | "outline";
  className?: string;
  isMenuCollapsed?: boolean;
  disabled?: boolean;
  disabledTooltip?: string;
}

const NewCaseDropdown = ({
  onRecordAudio,
  onUploadAudio,
  onBlankReport,
  showChecklist = false,
  onChecklist,
  buttonText = "New Case",
  buttonVariant = "primary",
  className = "",
  isMenuCollapsed = false,
  disabled = false,
  disabledTooltip,
}: NewCaseDropdownProps) => {
  const dropdownRef = useRef<HTMLDivElement>(null);

  const handleDropdownBlur = (e: React.FocusEvent) => {
    // Close dropdown when focus leaves
    if (!e.currentTarget.contains(e.relatedTarget as Node)) {
      const dropdown = dropdownRef.current?.querySelector('details');
      if (dropdown) {
        dropdown.removeAttribute('open');
      }
    }
  };

  const getButtonIcon = () => {
    if (buttonText === "Complete Checklist") {
      return <CheckCircleIcon className="size-6 mr-1" />;
    }
    // When the menu is collapsed the icon is the button's only content, so it
    // can never be hidden. Previously it was `hidden xl:inline-block`, which
    // under 1280px (e.g. any laptop at 150% browser zoom) left the collapsed
    // "New Case" button rendering as an empty blue rectangle.
    return (
      <DocumentPlusIcon
        className={cn(
          "size-6",
          isMenuCollapsed ? "" : "mr-1 hidden xl:inline-block",
        )}
      />
    );
  };

  const dropdown = (
    <div
      ref={dropdownRef}
      className={`dropdown ${className}`}
      onBlur={handleDropdownBlur}
    >
      <Button
        tabIndex={0}
        className="w-full"
        variant={buttonVariant}
        disabled={disabled}
      >
        <div
          className={cn(
            "flex items-center",
            isMenuCollapsed ? "justify-center" : "pl-2",
          )}
        >
          {getButtonIcon()}
          {!isMenuCollapsed && <p>{buttonText}</p>}
        </div>
      </Button>
      <ul
        tabIndex={0}
        className="dropdown-content menu bg-base-100 rounded-lg z-[1] w-52 p-2 shadow mt-2"
      >
        <li>
          <button onClick={onRecordAudio} data-tour="new-case-record" className="hover:bg-gray-100 dark:hover:bg-base-200">
            <MicrophoneIcon className="size-4" />
            Record Audio
          </button>
        </li>
        <li>
          <button onClick={onUploadAudio} data-tour="new-case-upload" className="hover:bg-gray-100 dark:hover:bg-base-200">
            <ArrowUpTrayIcon className="size-4" />
            Upload Files
          </button>
        </li>
        <li>
          <button onClick={onBlankReport} data-tour="new-case-blank" className="hover:bg-gray-100 dark:hover:bg-base-200">
            <DocumentIcon className="size-4" />
            Blank Report
          </button>
        </li>
        {showChecklist && onChecklist && (
          <li>
            <button onClick={onChecklist} className="hover:bg-gray-100 dark:hover:bg-base-200">
              <CheckCircleIcon className="size-4" />
              Checklist
            </button>
          </li>
        )}
      </ul>
    </div>
  );

  if (disabled && disabledTooltip) {
    return (
      <Tooltip overlay={disabledTooltip} placement="top">
        {dropdown}
      </Tooltip>
    );
  }

  return dropdown;
};

export default NewCaseDropdown;
